import React, { memo } from 'react';
import cn from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';

import { Form, Button, Input } from 'components/form';
import Dropdown from 'components/Dropdown';
import { Option } from 'lib/models/option';

import styles from './AddSelectOptionImage.module.scss';

interface Inputs {
  name: string;
}

export interface AddSelectOptionImageProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  options: Array<Option>;
  /** value */
  value?: string;
  /** placeholder */
  placeholder?: string;
  /** label */
  label?: string;
  /** onChange handler */
  onChange?: (value: string) => void;
  /** on remove handler */
  onRemove: (value: string) => void;
  /** on add custom value */
  onCustomAdd?: (input: Inputs) => Promise<unknown>;
  /** is custom input */
  isCustom?: boolean;
  /** custom placeholder */
  customPlaceholder?: string;
  /** is remove button loading */
  isRemoveLoading?: boolean;
  /** is save button loading */
  isSaveLoading?: boolean;
  /** make primary */
  onMakePrimary?: () => void;
  /** is primary */
  isPrimary?: boolean;
  /** label for primary option */
  primaryLabel?: string;
  /** title */
  title?: string;
}

export const AddSelectOptionImage: React.FC<AddSelectOptionImageProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    value = '',
    placeholder,
    onChange,
    onRemove,
    options,
    onCustomAdd,
    customPlaceholder,
    isCustom = false,
    isRemoveLoading = false,
    isSaveLoading = false,
    onMakePrimary,
    isPrimary,
    primaryLabel,
    title,
    label = '',
  }: AddSelectOptionImageProps) => {
    const contentClassNames = cn(styles.AddSelectOptionImage, className);

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty },
    } = useForm();

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
      if (onCustomAdd) {
        await onCustomAdd(data);
        reset();
      }
    };

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="AddSelectOptionImage"
      >
        {isCustom ? (
          <Form
            className={styles.AddSelectOptionImageForm}
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Input
              className={styles.AddSelectOptionImageFormInput}
              placeholder={customPlaceholder}
              {...register('name', {
                required: 'Required',
                validate: {
                  minValidCharacters: (val) => val.trim().length > 0,
                },
              })}
              isValid={!errors.name}
              helperText={
                errors.name?.message ||
                (errors.name?.type === 'minValidCharacters' &&
                  'Only spaces not allowed')
              }
            />
            <Button
              size="small"
              type="submit"
              disabled={!isDirty}
              isLoading={isSaveLoading}
              className={styles.AddSelectOptionImageFormSubmit}
            >
              Save
            </Button>
          </Form>
        ) : (
          <>
            <Dropdown
              title={title || ''}
              placeholder={placeholder}
              className={styles.AddSelectOptionImageDropdown}
              options={options}
              label={label}
              value={value}
              onChange={(item: string) => onChange && onChange(item)}
              alignment="right"
              disabled={!!value}
              hasPrimaryBg={!value}
              hideChevronIconOnDisabled
              triggerButtonClassName={
                styles.AddSelectOptionImageDropdownTriggerButton
              }
            />
            <Button
              color="danger"
              size="small"
              borderless
              className={cn(styles.AddSelectOptionImageButton, {
                [styles.AddSelectOptionImageButtonHidden]: !value,
              })}
              onClick={() => onRemove(value)}
              isLoading={isRemoveLoading}
              disabled={onMakePrimary && isPrimary}
            >
              Remove
            </Button>
            {onMakePrimary && (
              <div className={styles.AddSelectOptionImageMakePrimary}>
                {isPrimary ? (
                  <Button
                    className={styles.AddSelectOptionImagePrimary}
                    size="small"
                    variant="text"
                    color="primary"
                  >
                    {primaryLabel || 'Primary'}
                  </Button>
                ) : (
                  <Button
                    color="primary-light"
                    size="small"
                    borderless
                    onClick={onMakePrimary}
                  >
                    Make primary
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

AddSelectOptionImage.displayName = 'AddSelectOptionImage';
